/**
 * Composable per Orders autocomplete con loading e error handling centralizzati
 *
 * Fornisce funzionalità di ricerca asincrona di commesse (JobOrder) con OData query building
 * e loading state automatico. Integrabile in autocomplete input e filter component.
 *
 * @param options - Opzioni configurazione (withLoading per loading state centralizzato)
 * @returns Oggetto con orders reactive array e metodi di ricerca/pulizia
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { ExtendedAutocompleteOptions } from '@/lib/autocompletes';
import type { JobOrderAutocompleteResult } from '@/lib/models/jobOrder';
import { getOrdersAutocomplete } from '@/lib/autocompletes/orders';

export function useOrdersAutocomplete(options: BaseAutocompleteComposableOptions = {}) {
	const orders = ref<JobOrderAutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchOrders(options: ExtendedAutocompleteOptions = {}): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getOrdersAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					orders.value = result;
				}
			} catch (err) {
				console.error('❌ ERROR useOrdersAutocomplete.searchOrders:', err);
				error('Errore durante il caricamento delle commesse! Ricaricare la pagina e riprovare.');
				if (token.isLatest()) {
					orders.value = [];
				}
			}
		}, 'searchOrders');
	}

	function clearOrders(): void {
		orders.value = [];
	}

	return {
		orders: readonly(orders),
		searchOrders,
		clearOrders,
	};
}
